import React, { useEffect, useState, useCallback } from "react";
import { Plus, X, Trash2, RefreshCw, Tag } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

function CreatePromoModal({ onClose, onSaved, authHeaders }) {
  const [form, setForm] = useState({
    code: "", type: "Percent", value: "", description: "",
    minNights: 1, maxUses: "", validFrom: "", validTo: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  async function submit() {
    if (!form.code || !form.value) { setError("Code and value are required."); return; }
    setSaving(true); setError("");
    try {
      const res  = await fetch(`${API}/promo-codes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved(json.data); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  function field(label, key, type = "text", opts = {}) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
        <input type={type} value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
            focus:outline-none focus:ring-2 focus:ring-amber-400"
          {...opts} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">New Promo Code</h2>
          <button onClick={onClose}><X size={16} className="text-gray-500" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {field("Code *", "code", "text", { placeholder: "SUMMER10", style: { textTransform: "uppercase" } })}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
                <option value="Percent">Percent (%)</option>
                <option value="Flat">Flat (₹)</option>
              </select>
            </div>
            {field(form.type === "Percent" ? "Value (%) *" : "Value (₹) *", "value", "number", { placeholder: "10" })}
          </div>
          {field("Description", "description", "text", { placeholder: "Summer discount" })}
          <div className="grid grid-cols-2 gap-3">
            {field("Min Nights", "minNights", "number")}
            {field("Max Uses (blank = unlimited)", "maxUses", "number", { placeholder: "∞" })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field("Valid From", "validFrom", "date")}
            {field("Valid To", "validTo", "date")}
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>}
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500
              text-white text-sm font-medium hover:from-amber-600 hover:to-orange-600 disabled:opacity-60">
            {saving ? "Saving…" : "Create Code"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PromotionsPage() {
  const { authHeaders } = useAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/promo-codes`, { headers: authHeaders() });
      const json = await res.json();
      setPromos(json.data || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleActive(promo) {
    const res = await fetch(`${API}/promo-codes/${promo._id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ active: !promo.active }),
    });
    const json = await res.json();
    if (json.success) setPromos(prev => prev.map(p => p._id === promo._id ? json.data : p));
  }

  async function remove(id) {
    const res = await fetch(`${API}/promo-codes/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) setPromos(prev => prev.filter(p => p._id !== id));
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promotions</h1>
          <p className="text-sm text-gray-500">{promos.length} promo code{promos.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => setShowCreate(true)}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-white
              bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 rounded-xl
              shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all">
            <Plus size={15} /> New Code
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr className="border-b border-gray-100">
                {["Code","Discount","Min Nights","Used","Valid","Status","Actions"].map(h => (
                  <th key={h} className="text-left text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</td></tr>
              ) : promos.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">No promo codes yet</td></tr>
              ) : promos.map(p => (
                <tr key={p._id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 font-mono font-bold text-gray-900">
                      <Tag size={12} className="text-amber-500" /> {p.code}
                    </div>
                    {p.description && <p className="text-[10.5px] text-gray-400">{p.description}</p>}
                  </td>
                  <td className="px-3 py-2 text-gray-700">
                    {p.type === "Percent" ? `${p.value}%` : `₹${p.value}`}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-700">{p.minNights}</td>
                  <td className="px-3 py-2 text-gray-700">{p.usedCount}{p.maxUses ? ` / ${p.maxUses}` : ""}</td>
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                    {p.validFrom ? new Date(p.validFrom).toLocaleDateString("en-IN") : "Any"} → {p.validTo ? new Date(p.validTo).toLocaleDateString("en-IN") : "Any"}
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => toggleActive(p)}
                      className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border transition-colors
                        ${p.active ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => remove(p._id)} className="tap-44 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : promos.length === 0 ? (
            <p className="text-center py-12 text-gray-400">No promo codes yet</p>
          ) : (
            promos.map((p) => (
              <div key={p._id} className="p-3.5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 font-mono font-bold text-gray-900">
                      <Tag size={12} className="text-amber-500 flex-shrink-0" /> {p.code}
                    </div>
                    {p.description && <p className="text-[10.5px] text-gray-400">{p.description}</p>}
                  </div>
                  <button onClick={() => remove(p._id)} title="Delete"
                    className="tap-44 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex-shrink-0">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
                  <span className="font-semibold text-gray-900">{p.type === "Percent" ? `${p.value}%` : `₹${p.value}`} off</span>
                  <span className="text-gray-300">·</span>
                  <span>Min {p.minNights} night{p.minNights !== 1 ? "s" : ""}</span>
                  <span className="text-gray-300">·</span>
                  <span>Used {p.usedCount}{p.maxUses ? ` / ${p.maxUses}` : ""}</span>
                </div>
                <p className="text-[10.5px] text-gray-400">
                  {p.validFrom ? new Date(p.validFrom).toLocaleDateString("en-IN") : "Any"} → {p.validTo ? new Date(p.validTo).toLocaleDateString("en-IN") : "Any"}
                </p>
                <button onClick={() => toggleActive(p)}
                  className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border transition-colors
                    ${p.active ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                  {p.active ? "Active" : "Inactive"}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {showCreate && <CreatePromoModal onClose={() => setShowCreate(false)} onSaved={p => setPromos(prev => [p, ...prev])} authHeaders={authHeaders} />}
    </div>
  );
}
